import type { DataSource, InventoryRow, WriteRejection } from "@estelle/domain";
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { encodeFilter } from "@/components/filters/url";
import { dismissToast, showToast } from "@/components/shell/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  thresholdTargetOfRow,
  type ThresholdTarget,
} from "@/modules/inventory/threshold";

/** What the route stores up to; mirrored so the form can say so before a round trip. */
const MAX_THRESHOLD = 1_000_000;

const NOT_A_THRESHOLD =
  "Enter a whole number from 0 up, or leave it blank to use the store default.";

/**
 * Text from the box to what the route takes: blank clears the level's own
 * threshold (`null`), a whole number from 0 up sets it, anything else is
 * refused here rather than sent to be refused there.
 */
function parseThreshold(
  text: string,
): { value: number | null } | { problem: string } {
  const trimmed = text.trim();
  if (trimmed === "") return { value: null };
  if (!/^\d+$/.test(trimmed)) return { problem: NOT_A_THRESHOLD };
  const value = Number(trimmed);
  return value <= MAX_THRESHOLD
    ? { value }
    : {
        problem: `A threshold can be at most ${MAX_THRESHOLD.toLocaleString("en-US")}. Enter a smaller number, or leave it blank to use the store default.`,
      };
}

/** How long a save may sit on "Saving…" before the form says it has no answer. */
const SAVE_TIMEOUT_MS = 20_000;

/** The level, as a person names it: product, variant when it has one, warehouse. */
function levelName(target: ThresholdTarget): string {
  const variant = target.variant ? ` (${target.variant})` : "";
  return `${target.product}${variant}, ${target.warehouse}`;
}

/**
 * The toast, per level, that still offers to put a threshold back. A later
 * edit of the same level makes it wrong: Undo would restore the value from
 * before the first edit and silently discard the second (#548).
 */
const pendingUndo = new Map<string, number>();

function retireUndo(levelId: string): void {
  const earlier = pendingUndo.get(levelId);
  if (earlier !== undefined) dismissToast(earlier);
  pendingUndo.delete(levelId);
}

/** The sentence under the box when a write was refused. */
function refusalText(rejection: WriteRejection): string {
  return (
    (rejection.kind === "invalid" ? rejection.fields?.threshold : undefined) ??
    rejection.message
  );
}

/** Where the editor was opened from, which decides what a save has to say. */
type Listing = "levels" | "low-stock";

/** Refetch everything that counts or lists levels by their threshold. */
function refreshInventory(queryClient: QueryClient): void {
  // The row may now be on, or off, Low Stock, and the dashboard and the
  // Inventory report count what is on it.
  void queryClient.invalidateQueries({ queryKey: ["inventory"] });
  void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  void queryClient.invalidateQueries({ queryKey: ["reports"] });
}

/**
 * Saying that a level has left Low Stock, and giving it back (#521).
 *
 * Lowering a threshold on Low Stock takes the row off the page being looked at.
 * The row's controls go with it, so the page itself has nothing left to say
 * "it is not gone, it is fine now" or to offer the way back — a toast does,
 * with Undo, and a link to where the level now lives.
 *
 * Everything here is closed over rather than read from the component, because
 * the component is the row, and the row is what just unmounted. Undo writes
 * what was there before (`target.own`, which may be "none") through the same
 * data source and refreshes the same lists; it says so when it has.
 */
function announceLeftLowStock(
  target: ThresholdTarget,
  now: Pick<InventoryRow, "available" | "effectiveLowStockThreshold">,
  data: DataSource,
  queryClient: QueryClient,
): void {
  const where = levelName(target);

  function undo(): void {
    pendingUndo.delete(target.levelId);
    void data.inventory
      .setLowStockThreshold({
        levelId: target.levelId,
        threshold: target.own,
      })
      .then(
        (result) => {
          if (result.ok) {
            refreshInventory(queryClient);
            showToast({
              title: "Threshold restored",
              description: `${where} is back on Low Stock.`,
              durationMs: 5000,
            });
          } else {
            showToast({
              title: "Could not undo",
              description: refusalText(result.rejection),
              tone: "error",
            });
          }
        },
        () => {
          showToast({
            title: "Could not undo",
            description: "Check your connection and try again.",
            tone: "error",
          });
        },
      );
  }

  // The level, found where Stock Levels finds anything: its SKU (or, with
  // none, its product's name and its variant) at its warehouse.
  const search: Record<string, string> = { q: target.sku ?? target.product };
  const filter = encodeFilter({
    kind: "group",
    combinator: "and",
    children: [
      {
        kind: "condition",
        field: "warehouse",
        operator: "is",
        value: target.warehouse,
      },
      ...(target.sku === null && target.variant !== null
        ? [
            {
              kind: "condition" as const,
              field: "variantTitle",
              operator: "is" as const,
              value: target.variant,
            },
          ]
        : []),
    ],
  });
  if (filter) search.filter = filter;

  const toastId = showToast({
    title: "Moved out of Low Stock",
    description: `${where}: ${String(now.available)} available is above its threshold of ${String(now.effectiveLowStockThreshold)}.`,
    action: { label: "Undo", onClick: undo },
    link: { label: "Show in Stock Levels", to: "/inventory", search },
    durationMs: 12_000,
  });
  pendingUndo.set(target.levelId, toastId);
}

/**
 * The threshold a stock level is judged by, drawn as the one thing it is: a
 * number, and "default" beside it when the level has none of its own.
 *
 * For a person who may edit it (`inventory.lowstock.edit`) the number is a
 * button that opens a small form — a threshold belongs to one variant at one
 * warehouse, so the control is on the row that has it rather than in a
 * settings page the team would have to find and cross-reference.
 *
 * `listing` says which list it sits in. Only on Low Stock does a lower
 * threshold make the row leave, and only there does the save say so.
 *
 * The write is not spliced into the list: the form closes on success and the
 * inventory lists are refetched, so what is on screen is what the database now
 * holds (the same choice `Adjustments` makes). A refusal leaves the form open
 * with the server's own words and changes nothing.
 */
export function ThresholdControl({
  row,
  className,
  listing = "levels",
}: {
  row: InventoryRow;
  /** The button's own spacing — a padded box that would only knock a plain span off the baseline. */
  className?: string;
  listing?: Listing;
}) {
  const own = row.variant.lowStockThreshold;
  const label = (
    <>
      <span className="tabular-nums">{row.effectiveLowStockThreshold}</span>
      {own === null ? (
        <span className="text-muted-foreground ml-1 text-xs">default</span>
      ) : null}
    </>
  );

  const permissions = usePermissions();
  if (!can(permissions, "inventory.lowstock.edit")) {
    return <span>{label}</span>;
  }

  const target = thresholdTargetOfRow(row);
  return (
    <ThresholdPopover target={target} listing={listing}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(TOUCH_TARGET, className ?? "-mr-2 px-2")}
        aria-label={`Threshold ${String(target.effective)}${own === null ? " (store default)" : ""} for ${target.product}, ${target.warehouse}. Change it.`}
      >
        {label}
        <Pencil className="text-muted-foreground size-3" aria-hidden />
      </Button>
    </ThresholdPopover>
  );
}

/**
 * The same editor from a By warehouse cell, where the cell is a quantity and
 * has no room to show a threshold beside it: a pencil, beside the figure, that
 * opens the form for that variant at that warehouse (#521).
 *
 * Faint until the row is hovered or the pencil focused, so a grid of figures is
 * not a grid of pencils; always shown where there is no hover to reveal it.
 * Nothing at all for someone who may not edit.
 */
export function ThresholdCellButton({
  target,
  className,
}: {
  target: ThresholdTarget;
  className?: string;
}) {
  const permissions = usePermissions();
  if (!can(permissions, "inventory.lowstock.edit")) return null;

  return (
    <ThresholdPopover target={target} listing="levels">
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        className={cn(
          "text-muted-foreground opacity-40 transition-opacity",
          "hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
          "in-[tr:hover]:opacity-100 in-[tr:focus-within]:opacity-100",
          // A finger-sized hit area that takes up no more room than the icon.
          "pointer-coarse:-m-3 pointer-coarse:size-11 pointer-coarse:opacity-100",
          className,
        )}
        aria-label={`Low stock threshold ${String(target.effective)}${target.own === null ? " (store default)" : ""} for ${target.product}, ${target.warehouse}. Change it.`}
      >
        <Pencil aria-hidden />
      </Button>
    </ThresholdPopover>
  );
}

/**
 * At least 44px square wherever a finger is the input. The visible control is
 * the same size either way; `-my-1.5` takes back the room the hit area adds, so
 * a table row is no taller on a tablet than it is under a mouse.
 */
const TOUCH_TARGET =
  "h-8 pointer-coarse:h-11 pointer-coarse:min-w-11 pointer-coarse:-my-1.5";

function ThresholdPopover({
  target,
  listing,
  children,
}: {
  target: ThresholdTarget;
  listing: Listing;
  /** The trigger: a button, which the popover opens from. */
  children: ReactNode;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const inputId = useId();

  const own = target.own;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(own === null ? "" : String(own));
  const [problem, setProblem] = useState<string | null>(null);

  const mutation = useMutation({
    // A request that never answers (the network gone mid-save) would sit on
    // "Saving…" until it comes back. Past the limit the form says so; the
    // request itself is not cancelled, so the message asks for a look at the
    // level before trying again rather than claiming nothing was written.
    mutationFn: (threshold: number | null) =>
      Promise.race([
        data.inventory.setLowStockThreshold({
          levelId: target.levelId,
          threshold,
        }),
        new Promise<never>((_resolve, reject) => {
          setTimeout(() => {
            reject(new Error("timeout"));
          }, SAVE_TIMEOUT_MS);
        }),
      ]),
  });

  function reset(isOpen: boolean) {
    setOpen(isOpen);
    if (isOpen) {
      setText(own === null ? "" : String(own));
      setProblem(null);
      mutation.reset();
    }
  }

  function save(threshold: number | null) {
    setProblem(null);
    // Nothing to wait for: the request would only queue until the connection
    // returned, and the form would say "Saving…" the whole time.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setProblem("You are offline. Reconnect, then save again.");
      return;
    }
    mutation.mutate(threshold, {
      onSuccess: (result) => {
        if (result.ok) {
          setOpen(false);
          // A newer edit of this level makes any earlier Undo wrong.
          retireUndo(target.levelId);
          refreshInventory(queryClient);

          // The row is about to leave the list it is on — said, with the way
          // back, since nothing on that list will be left to say it.
          const wasLow = target.available <= target.effective;
          const nowLow =
            result.data.available <= result.data.effectiveLowStockThreshold;
          if (listing === "low-stock" && wasLow && !nowLow) {
            announceLeftLowStock(target, result.data, data, queryClient);
          }
        } else {
          setProblem(refusalText(result.rejection));
        }
      },
      onError: (error) => {
        setProblem(
          error.message === "timeout"
            ? "No answer yet. Check your connection and look at this level's threshold before saving again."
            : "Could not save the threshold. Check your connection.",
        );
      },
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = parseThreshold(text);
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    save(parsed.value);
  }

  const where = levelName(target);

  return (
    <Popover open={open} onOpenChange={reset}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/*
        `collisionPadding` and the width cap keep it inside the screen: on a
        phone the form is wider than the room to the left of a control near the
        middle of the card, and it opened flush against the edge (#521).
      */}
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-72 max-w-[calc(100vw-2rem)]"
      >
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-3"
          noValidate
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor={inputId}>Low when available is at or below</Label>
            <p className="text-muted-foreground truncate text-xs">{where}</p>
          </div>
          <Input
            id={inputId}
            inputMode="numeric"
            autoComplete="off"
            placeholder="Store default"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setProblem(null);
            }}
            aria-invalid={problem !== null}
            aria-describedby={problem ? `${inputId}-problem` : undefined}
            className="pointer-coarse:h-11"
          />
          {problem ? (
            <p
              id={`${inputId}-problem`}
              role="alert"
              className="text-destructive text-xs"
            >
              {problem}
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              Leave it blank to use the store default.
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            {own !== null ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="pointer-coarse:h-11"
                disabled={mutation.isPending}
                onClick={() => {
                  save(null);
                }}
              >
                Use default
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              size="sm"
              className="pointer-coarse:h-11 pointer-coarse:px-5"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
